import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { CartService } from '../../services/cart.service';
import { AuthService } from '../../services/auth.service';
import { OrderService } from '../../services/order.service';
import { DoseColorService } from '../../services/dose-color.service';
import { RazorpayService } from '../../services/razorpay.service';
import { VyaparGatewayService } from '../../services/vyapargateway.service';
import { ShiprocketService } from '../../services/shiprocket.service';
import { LanguageService } from '../../services/language.service';
import { ProductLocalizationService } from '../../services/product-localization.service';
import { CategoryLocalizationService } from '../../services/category-localization.service';
import { ToastService } from '../../services/toast.service';
import { CartItem, User, Order, DoseType } from '../../interfaces/product.interface';
import { environment } from '../../../environments/environment';

declare var Razorpay: any;

@Component({
  selector: 'app-checkout',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './checkout.html',
  styleUrl: './checkout.scss'
})
export class Checkout implements OnInit {
  checkoutForm: FormGroup;
  cartItems: CartItem[] = [];
  currentUser: User | null = null;
  subtotal = 0;
  discount = 0;
  deliveryCharges = 0;
  total = 0;
  isLoading = false;
  successMessage = '';
  currentStep = 1; // 1: Address, 2: Payment, 3: Confirmation
  selectedPaymentMethod = 'vyapargateway'; // 'vyapargateway', 'razorpay', or 'cod'
  orderPlaced = false;
  orderId = '';
  paymentError = false;
  errorMessage = '';
  isCalculatingDelivery = false;
  deliveryChargesFetched = false;
  availableCouriers: any[] = [];
  selectedCourier: any = null;
  codNotAvailableReason = ''; // Store reason why COD is not available

  // VyaparGateway specific properties
  showVyaparQR = false;
  vyaparQRCode = '';
  vyaparOrderId = '';
  vyaparTransactionId = '';

  // Updated payment methods - all handled by Razorpay
  paymentMethods = [] as Array<{ id: string; name: string; description: string; icon: string }>;

  constructor(
    private fb: FormBuilder,
    private cartService: CartService,
    private authService: AuthService,
    private orderService: OrderService,
    private doseColorService: DoseColorService,
    private razorpayService: RazorpayService,
    private vyaparGatewayService: VyaparGatewayService,
    private shiprocketService: ShiprocketService,
    private languageService: LanguageService,
    private productLocalizationService: ProductLocalizationService,
    private categoryLocalizationService: CategoryLocalizationService,
    private toastService: ToastService,
    private router: Router
  ) {
    this.checkoutForm = this.fb.group({
      // Delivery Address with Enhanced Validation
      fullName: ['', [
        Validators.required, 
        Validators.minLength(2), 
        Validators.maxLength(50),
        Validators.pattern(/^[a-zA-Z\s\u0900-\u097F]+$/) // Allow English and Hindi characters
      ]],
      mobile: ['', [
        Validators.required, 
        Validators.pattern(/^[6-9]\d{9}$/),
        this.mobileNumberValidator
      ]],
      altMobile: ['', [
        Validators.pattern(/^[6-9]\d{9}$/),
        this.mobileNumberValidator
      ]],
      address: ['', [
        Validators.required, 
        Validators.minLength(10),
        Validators.maxLength(200),
        this.addressValidator
      ]],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      pincode: ['', [
        Validators.required, 
        Validators.pattern(/^\d{6}$/), 
        this.pincodeValidator
      ]],
      paymentMethod: ['vyapargateway', [Validators.required]] // 'vyapargateway', 'razorpay', or 'cod'
    });
  }

  // Custom Validators
  mobileNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    
    const mobile = control.value.toString();
    
    // Check for common invalid patterns
    if (mobile.length !== 10) {
      return { invalidLength: true };
    }
    
    // Check for repeated digits (like 1111111111)
    if (/^(\d)\1{9}$/.test(mobile)) {
      return { repeatedDigits: true };
    }
    
    // Check for sequential digits (like 1234567890)
    if (/^(?:0123456789|9876543210)$/.test(mobile)) {
      return { sequentialDigits: true };
    }
    
    return null;
  }

  addressValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    
    const address = control.value.toString().trim();
    
    // Check for minimum meaningful content
    if (address.length < 10) {
      return { tooShort: true };
    }
    
    // Check for valid address format (should contain letters and numbers)
    if (!/^[a-zA-Z0-9\s\u0900-\u097F.,-]+$/.test(address)) {
      return { invalidFormat: true };
    }
    
    // Check for suspicious patterns (all same character)
    if (/^(.)\1+$/.test(address.replace(/\s/g, ''))) {
      return { suspiciousPattern: true };
    }
    
    return null;
  }

  pincodeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    
    const pincode = control.value.toString();
    
    // Additional validation for pincode format
    if (!/^\d{6}$/.test(pincode)) {
      return { invalidFormat: true };
    }
    
    // Check for invalid pincode patterns
    if (/^(\d)\1{5}$/.test(pincode)) { // All same digits
      return { invalidPattern: true };
    }
    
    return null;
  }

  // Translation method
  t(key: string, params?: any): string {
    return this.languageService.t(key, params);
  }

  ngOnInit(): void {
    // Check if user is logged in
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login'], { 
        queryParams: { returnUrl: '/checkout' } 
      });
      return;
    }

    // Initialize translated payment method labels
    this.rebuildPaymentMethods();

    // Rebuild translated payment method labels when language changes
    this.languageService.currentLanguage$.subscribe(() => {
      this.rebuildPaymentMethods();
    });

    this.loadCheckoutData();
    
    // Setup pincode change listener
    this.setupPincodeListener();
  }
  
  private setupPincodeListener(): void {
    this.checkoutForm.get('pincode')?.valueChanges.subscribe(pincode => {
      if (pincode && pincode.length === 6) {
        this.fetchCityFromPincode(pincode);
      }
    });

    // Listen to payment method changes to recalculate delivery charges
    this.checkoutForm.get('paymentMethod')?.valueChanges.subscribe(paymentMethod => {
      this.selectedPaymentMethod = paymentMethod;
      const pincode = this.checkoutForm.get('pincode')?.value;
      
      // Recalculate delivery charges if pincode is valid
      if (pincode && pincode.length === 6 && this.checkoutForm.get('city')?.value) {
        this.fetchDeliveryCharges(pincode);
      }
    });
  }
  
  private fetchCityFromPincode(pincode: string): void {
    // Using India Post Pincode API
    fetch(`https://api.postalpincode.in/pincode/${pincode}`)
      .then(response => response.json())
      .then(data => {
        if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice) {
          const postOffice = data[0].PostOffice[0];
          const city = postOffice.District || postOffice.Block;
          const state = postOffice.State;
          
          // Auto-populate city and state for all locations
          this.checkoutForm.patchValue({
            city: city,
            state: state
          });
          
          // Fetch delivery charges after successful pincode detection
          this.fetchDeliveryCharges(pincode);
        } else {
          this.toastService.warning(this.t('couldNotFetchLocation'));
        }
      })
      .catch(error => {
        // Silently fail - user can enter manually
      });
  }

  private fetchDeliveryCharges(pincode: string): void {
    this.isCalculatingDelivery = true;

    // Calculate total weight (default 0.5 kg per item, you can customize this)
    const totalWeight = this.cartItems.length * 0.5;

    // Get payment method to check if COD is selected
    const isCOD = this.checkoutForm.get('paymentMethod')?.value === 'cod';
    const codFlag: 0 | 1 = isCOD ? 1 : 0;

    this.shiprocketService.checkServiceability(pincode, totalWeight, codFlag).subscribe({
      next: (response) => {
        this.isCalculatingDelivery = false;
        
        if (response.data?.available_courier_companies && response.data.available_courier_companies.length > 0) {
          this.availableCouriers = response.data.available_courier_companies;
          
          // ALWAYS check COD availability regardless of selected payment method
          const codCouriers = this.availableCouriers.filter((courier: any) => {
            return courier.cod === 1;
          });
          
          // Set COD availability message
          if (codCouriers.length === 0) {
            this.codNotAvailableReason = this.t('noCourierPartnersSupportCOD');
          } else {
            this.codNotAvailableReason = ''; // COD is available
          }
          
          // Filter couriers based on current payment method
          let filteredCouriers = this.availableCouriers;
          if (isCOD) {
            filteredCouriers = codCouriers;
          }

          if (filteredCouriers.length === 0) {
            // If COD selected but no COD couriers available
            this.toastService.warning(this.t('noCODDeliveryAvailableForPincode'));
            this.deliveryCharges = 0;
            this.deliveryChargesFetched = false;
            this.selectedCourier = null;
            // Auto-switch to online payment
            this.selectPaymentMethod('online');
            // Recalculate with online payment (non-COD)
            setTimeout(() => {
              this.fetchDeliveryCharges(pincode);
            }, 100);
          } else {
            // Select cheapest courier from filtered list
            const cheapestCourier = filteredCouriers.reduce((prev: any, curr: any) => 
              prev.rate < curr.rate ? prev : curr
            );
            
            this.selectedCourier = cheapestCourier;
            this.deliveryCharges = Math.round(cheapestCourier.rate);
            this.deliveryChargesFetched = true;
          }
        } else {
          this.toastService.warning(this.t('deliveryNotAvailableForPincode'));
          this.deliveryCharges = 0;
          this.deliveryChargesFetched = false;
          this.selectedCourier = null;
          this.codNotAvailableReason = this.t('deliveryNotAvailableForYourPincode');
          // Auto-switch to online payment if COD was selected
          if (this.selectedPaymentMethod === 'cod') {
            this.selectPaymentMethod('online');
          }
        }
        
        this.calculateTotals();
      },
      error: (error) => {
        this.isCalculatingDelivery = false;
        this.deliveryCharges = 0;
        this.deliveryChargesFetched = false;
        this.selectedCourier = null;
        this.codNotAvailableReason = this.t('unableToCheckDeliveryAvailability');
        // Silent on background calculation failures; block on Place Order instead
        // Auto-switch to online payment if COD was selected
        if (this.selectedPaymentMethod === 'cod') {
          this.selectPaymentMethod('online');
        }
        this.calculateTotals();
      }
    });
  }

  loadCheckoutData(): void {
    // Load current user
    this.currentUser = this.authService.getCurrentUser();
    if (this.currentUser) {
      this.populateUserData();
    }

    // Load cart items
    this.cartService.getCartItems().subscribe(items => {
      this.cartItems = items;
      if (this.cartItems.length === 0) {
        this.toastService.warning('Your cart is empty. Please add items before checkout.');
        this.router.navigate(['/cart']);
        return;
      }
      this.calculateTotals();
      this.toastService.info(`Ready to checkout ${this.cartItems.length} item(s) for ₹${this.total}`, { duration: 3000 });
    });
  }

  populateUserData(): void {
    if (!this.currentUser) return;

    this.checkoutForm.patchValue({
      fullName: this.currentUser.name,
      mobile: this.currentUser.mobile,
      altMobile: this.currentUser.altMobile || '',
      address: this.currentUser.fullAddress
    });
  }

  calculateTotals(): void {
    this.subtotal = this.cartItems.reduce((total, item) => 
      total + ((item.price ?? 0) * item.cartQuantity), 0
    );

    // Calculate combo discount
    this.discount = this.orderService.calculateComboDiscount(this.cartItems);
    
    // Delivery charges are set when pincode is entered and serviceability is checked
    // Don't reset to 0 here as it will override the calculated charges
    
    this.total = this.subtotal - this.discount + this.deliveryCharges;
  }

  // Step Navigation
  nextStep(): void {
    if (this.currentStep === 1) {
      if (this.validateAddressForm()) {
        this.currentStep = 2;
        this.toastService.success('Address details saved! Proceeding to payment.');
      }
    } else if (this.currentStep === 2) {
      this.currentStep = 3;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step <= this.currentStep || this.orderPlaced) {
      this.currentStep = step;
    }
  }

  // Enhanced Validation
  validateAddressForm(): boolean {
    const addressFields = ['fullName', 'mobile', 'address', 'city', 'state', 'pincode'];
    let isValid = true;
    let errorMessages: string[] = [];

    addressFields.forEach(field => {
      const control = this.checkoutForm.get(field);
      if (control && control.invalid) {
        control.markAsTouched();
        isValid = false;
        
        // Collect specific error messages for better UX
        const errors = control.errors;
        if (errors) {
          if (errors['required']) {
            errorMessages.push(`${this.getFieldDisplayName(field)} is required`);
          } else if (errors['minlength']) {
            errorMessages.push(`${this.getFieldDisplayName(field)} is too short`);
          } else if (errors['maxlength']) {
            errorMessages.push(`${this.getFieldDisplayName(field)} is too long`);
          } else if (errors['pattern']) {
            errorMessages.push(`${this.getFieldDisplayName(field)} format is invalid`);
          } else if (errors['repeatedDigits']) {
            errorMessages.push('Mobile number cannot have all same digits');
          } else if (errors['sequentialDigits']) {
            errorMessages.push('Mobile number cannot be sequential');
          } else if (errors['suspiciousPattern']) {
            errorMessages.push('Address contains suspicious pattern');
          }
        }
      }
    });

    // Show comprehensive error message
    if (!isValid && errorMessages.length > 0) {
      // this.toastService.error(`Please fix the following issues: ${errorMessages.join(', ')}`);
    }

    return isValid;
  }

  private getFieldDisplayName(field: string): string {
    const fieldNames: { [key: string]: string } = {
      'fullName': 'Full Name',
      'mobile': 'Mobile Number',
      'address': 'Address',
      'city': 'City',
      'state': 'State',
      'pincode': 'Pincode'
    };
    return fieldNames[field] || field;
  }

  // Payment Method Selection
  selectPaymentMethod(method: string): void {
    this.selectedPaymentMethod = method;
    this.checkoutForm.patchValue({ paymentMethod: method });
    
    // Hide Vyapar QR when switching payment methods
    if (method !== 'vyapargateway') {
      this.showVyaparQR = false;
      this.vyaparQRCode = '';
    }
    
    if (method === 'cod') {
      this.toastService.info(this.t('cashOnDelivery') || 'Cash on Delivery');
    } else if (method === 'vyapargateway') {
      this.toastService.info('UPI Payment selected - 0% fees');
    } else if (method === 'razorpay') {
      this.toastService.info('Card/Net Banking/Wallet payment selected');
    }
  }

  // Place Order
  async placeOrder(): Promise<void> {
    if (!this.validateAddressForm()) {
      this.toastService.error('Please fill in all required address fields correctly.');
      return;
    }

    if (!this.validatePaymentForm()) {
      return; // validatePaymentForm already shows toast error
    }

    // Check if delivery charges are fetched
    if (!this.deliveryChargesFetched) {
      this.toastService.error('Please enter a valid pincode to calculate delivery.');
      return;
    }

    if (!this.selectedCourier) {
      this.toastService.error('No delivery partner available for your location.');
      return;
    }

    // Validate COD selection
    if (this.selectedPaymentMethod === 'cod') {
      // Double-check that selected courier supports COD
      if (!this.selectedCourier.cod || this.selectedCourier.cod !== 1) {
        this.toastService.error(this.t('selectedCourierDoesNotSupportCOD'));
        // Auto-switch to online payment
        this.selectPaymentMethod('online');
        return;
      }
    }

    this.isLoading = true;
    const deliveryAddress = this.getFormattedAddress();
    const isComboOrder = this.cartItems.length > 1;

    // Check payment method
    if (this.selectedPaymentMethod === 'cod') {
      this.toastService.info('Placing COD order...');
      // Process COD order directly
      this.processCODOrder(deliveryAddress, isComboOrder);
    } else if (this.selectedPaymentMethod === 'vyapargateway') {
      // Process VyaparGateway payment
      this.processVyaparPayment(deliveryAddress, isComboOrder);
    } else {
      // Process Razorpay payment
      // Wait for Razorpay to be ready before proceeding
      try {
        await this.waitForRazorpayReady();
      } catch (error) {
        this.toastService.error('Payment gateway is not ready. Please refresh the page and try again.');
        this.isLoading = false;
        return;
      }

      // Process online payment through Razorpay
      this.processRazorpayPayment(deliveryAddress, isComboOrder);
    }
  }



  private processCODOrder(deliveryAddress: string, isComboOrder: boolean): void {
    // Prepare payment details for COD order
    const paymentDetails = {
      payment_id: 'COD',
      order_id: 'COD-' + Date.now(),
      status: 'pending',
      method: 'cod',
      amount: this.total,
      verified: true,
      courier: this.selectedCourier
    };

    this.orderService.placeOrder(
      this.cartItems,
      deliveryAddress,
      'cod',
      isComboOrder,
      this.discount,
      paymentDetails,
      this.deliveryCharges
    ).subscribe({
      next: response => {
        this.isLoading = false;

        if (response.success && response.order) {
          this.processSuccessfulOrder(response.order, 'Order placed successfully! You will pay on delivery.');
        } else {
          this.toastService.error('Order could not be placed. Please try again.');
          this.isLoading = false;
        }
      },
      error: error => {
        this.isLoading = false;
        this.toastService.error('Order placement failed. Please try again.');
      }
    });
  }

  private processVyaparPayment(deliveryAddress: string, isComboOrder: boolean): void {
    const form = this.checkoutForm.value;
    
    // Validate inputs before proceeding
    if (!this.vyaparGatewayService.validateAmount(this.total)) {
      this.isLoading = false;
      this.toastService.error('Invalid payment amount. Please check your order total.');
      return;
    }

    if (!this.vyaparGatewayService.validatePhoneNumber(form.mobile)) {
      this.isLoading = false;
      this.toastService.error('Invalid phone number format. Please enter a valid 10-digit mobile number.');
      return;
    }

    const paymentRequest = {
      amount: this.total,
      customer_name: form.fullName,
      customer_email: this.currentUser?.email || '',
      customer_phone: form.mobile,
      order_id: this.vyaparGatewayService.generateOrderId()
    };

    this.vyaparGatewayService.createOrder(paymentRequest).subscribe({
      next: (orderResponse) => {
        if (orderResponse.success && (orderResponse.payment_url || orderResponse.qr_code)) {
          this.vyaparOrderId = orderResponse.order_id || '';
          this.vyaparQRCode = orderResponse.qr_code || '';
          
          // Check device type and handle accordingly
          if (this.vyaparGatewayService.isMobileDevice() && orderResponse.payment_url) {
            // Mobile: Open UPI app via intent
            this.toastService.info('Opening UPI app...');
            window.location.href = orderResponse.payment_url!;
            
            // Start polling for payment status
            this.pollVyaparPaymentStatus(deliveryAddress, isComboOrder);
          } else {
            // Desktop: Show QR code
            this.showVyaparQR = true;
            this.isLoading = false;
            this.toastService.info('Scan QR code to pay');
          }
        } else {
          this.isLoading = false;
          this.toastService.error('Failed to create payment order. Please try again.');
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.toastService.error('Failed to create payment order. Please try again.');
      }
    });
  }

  private pollVyaparPaymentStatus(deliveryAddress: string, isComboOrder: boolean): void {
    const maxAttempts = 30; // 5 minutes
    let attempts = 0;

    const pollInterval = setInterval(() => {
      attempts++;
      
      this.vyaparGatewayService.verifyPayment(this.vyaparOrderId).subscribe({
        next: (response) => {
          if (response.verified) {
            clearInterval(pollInterval);
            this.processSuccessfulVyaparPayment(deliveryAddress, isComboOrder);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            this.isLoading = false;
            this.toastService.error('Payment verification timeout. Please try again.');
          }
        },
        error: (error) => {
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            this.isLoading = false;
            this.toastService.error('Payment verification failed. Please try again.');
          }
        }
      });
    }, 10000); // Poll every 10 seconds
  }

  private processSuccessfulVyaparPayment(deliveryAddress: string, isComboOrder: boolean): void {
    const paymentDetails = {
      payment_id: this.vyaparOrderId,
      order_id: this.vyaparOrderId,
      status: 'success',
      method: 'vyapargateway',
      amount: this.total,
      verified: true,
      courier: this.selectedCourier
    };

    this.orderService.placeOrder(
      this.cartItems,
      deliveryAddress,
      'vyapargateway',
      isComboOrder,
      this.discount,
      paymentDetails,
      this.deliveryCharges
    ).subscribe({
      next: response => {
        this.isLoading = false;
        
        if (response.success && response.order) {
          this.processSuccessfulOrder(response.order, 'Payment successful! Order placed successfully.');
        } else {
          this.toastService.error('Order could not be created. Please contact support.');
        }
      },
      error: error => {
        this.isLoading = false;
        this.toastService.error('Order creation failed. Please contact support.');
      }
    });
  }

  // VyaparGateway UI methods
  cancelVyaparPayment(): void {
    this.showVyaparQR = false;
    this.vyaparQRCode = '';
    this.vyaparOrderId = '';
    this.isLoading = false;
  }

  checkVyaparPaymentStatus(): void {
    if (!this.vyaparOrderId) {
      this.toastService.error('No active payment to check');
      return;
    }

    this.isLoading = true;
    const deliveryAddress = this.getFormattedAddress();
    const isComboOrder = this.cartItems.length > 1;

    this.vyaparGatewayService.verifyPayment(this.vyaparOrderId).subscribe({
      next: (response) => {
        if (response.verified) {
          this.showVyaparQR = false;
          this.processSuccessfulVyaparPayment(deliveryAddress, isComboOrder);
        } else {
          this.isLoading = false;
          this.toastService.error('Payment not yet completed. Please scan the QR code and complete payment.');
        }
      },
      error: error => {
        this.isLoading = false;
        this.toastService.error('Failed to check payment status. Please try again.');
      }
    });
  }

  private processRazorpayPayment(deliveryAddress: string, isComboOrder: boolean): void {
    const form = this.checkoutForm.value;
    
    // Validate inputs before proceeding
    if (!this.razorpayService.validateAmount(this.total)) {
      this.isLoading = false;
      this.toastService.error('Invalid payment amount. Please check your order total.');
      return;
    }

    if (!this.razorpayService.validatePhoneNumber(form.mobile)) {
      this.isLoading = false;
      this.toastService.error('Invalid phone number format. Please enter a valid 10-digit mobile number.');
      return;
    }

    // Ensure Razorpay is ready before proceeding
    this.razorpayService.ensureRazorpayReady().then((isReady) => {
      if (!isReady) {
        this.isLoading = false;
        this.toastService.error('Payment gateway is not ready. Please refresh the page and try again.');
        return;
      }

      const paymentRequest = {
        amount: this.total,
        // amount: 1,
        currency: 'INR',
        receipt: this.razorpayService.generateReceiptId(),
        notes: {
          customer_name: form.fullName,
          customer_email: this.currentUser?.email || '',
          customer_phone: form.mobile,
          delivery_address: deliveryAddress
        }
      };

      this.razorpayService.createOrder(paymentRequest).subscribe({
        next: (orderResponse) => {
          if (orderResponse.success && orderResponse.order_id) {
            this.toastService.info('Opening payment...');
            this.razorpayService.initiatePayment(
              orderResponse.order_id,
              this.total,
              form.fullName,
              this.currentUser?.email || '',
              form.mobile,
              deliveryAddress
            ).subscribe({
              next: (paymentResponse) => {
                if (paymentResponse.success && paymentResponse.status === 'success') {
                  // Verify payment signature and status before processing
                  this.verifyAndProcessPayment(paymentResponse, deliveryAddress, isComboOrder);
                } else {
                  // Reset loading state immediately for failed/cancelled payments
                  this.isLoading = false;
                  
                  let errorMsg = '';
                  let isCancelled = false;
                  let status = 'failed';
                  
                  // Check if payment was cancelled by user
                  if (paymentResponse.status === 'cancelled' || paymentResponse.status === 'dismissed') {
                    isCancelled = true;
                    status = 'cancelled';
                    errorMsg = 'You cancelled the payment.';
                  } else if (paymentResponse.status === 'failed') {
                    errorMsg = this.razorpayService.handlePaymentError(paymentResponse);
                  } else if (paymentResponse.status === 'timeout') {
                    errorMsg = 'Payment timed out.';
                  } else {
                    errorMsg = this.razorpayService.handlePaymentError(paymentResponse) || 'Payment failed.';
                  }
                  
                  // Redirect to payment status page
                  this.redirectToPaymentStatus(status as 'success' | 'failed' | 'cancelled', paymentResponse, errorMsg);
                  
                  // Log failed payment for tracking
                  this.logFailedPayment(paymentResponse, deliveryAddress, isComboOrder);
                }
              },
              error: (error) => {
                this.isLoading = false;
                this.orderPlaced = false;
                const errorMsg = this.razorpayService.handlePaymentError(error);
                this.toastService.error(errorMsg);
                this.currentStep = 2;
              }
            });
          } else {
            this.isLoading = false;
            this.orderPlaced = false;
            const errorMsg = orderResponse.error || 'Failed to create payment order';
            this.toastService.error(errorMsg);
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.orderPlaced = false;
          this.toastService.error('Failed to create payment order. Please try again.');
        }
      });
    });
  }

  private verifyAndProcessPayment(
    paymentResponse: any, 
    deliveryAddress: string, 
    isComboOrder: boolean
  ): void {
    // First verify payment signature
    this.razorpayService.verifyPaymentSignature(
      paymentResponse.order_id,
      paymentResponse.payment_id,
      paymentResponse.signature
    ).subscribe({
      next: (verificationResponse) => {
        if (verificationResponse.success && verificationResponse.verified) {
          // Payment signature verified successfully
          this.toastService.success('Payment verified successfully!');
          
          // Now check payment status from Razorpay
          this.checkPaymentStatusAndProcess(paymentResponse, deliveryAddress, isComboOrder);
        } else {
          this.toastService.error('Payment verification failed. Please contact support.');
          this.isLoading = false;
          this.currentStep = 2;
          this.orderPlaced = false;
        }
      },
      error: (error) => {
        this.toastService.error('Payment verification failed. Please contact support.');
        this.isLoading = false;
        this.currentStep = 2;
        this.orderPlaced = false;
      }
    });
  }

  private checkPaymentStatusAndProcess(
    paymentResponse: any, 
    deliveryAddress: string, 
    isComboOrder: boolean
  ): void {
    // Check payment status from Razorpay
    this.razorpayService.checkPaymentStatus(paymentResponse.payment_id).subscribe({
      next: (statusResponse) => {
        if (statusResponse.success && statusResponse.verified) {
          this.processSuccessfulPayment(paymentResponse, deliveryAddress, isComboOrder);
        } else {
          this.toastService.error('Payment status verification failed. Please contact support.');
          this.isLoading = false;
          this.currentStep = 2;
          this.orderPlaced = false;
        }
      },
      error: (error) => {
        this.toastService.error('Failed to verify payment status. Please contact support.');
        this.isLoading = false;
        this.currentStep = 2;
        this.orderPlaced = false;
      }
    });
  }

  private processSuccessfulPayment(
    paymentResponse: any, 
    deliveryAddress: string, 
    isComboOrder: boolean
  ): void {
    // Create order with payment details
    this.toastService.info('Placing your order...', { duration: 2000 });
    
    // Prepare payment details for order creation
    const paymentDetails = {
      payment_id: paymentResponse.payment_id,
      order_id: paymentResponse.order_id,
      signature: paymentResponse.signature,
      status: 'success',
      method: 'razorpay',
      amount: this.total,
      verified: true,
      courier: this.selectedCourier
    };
    
    this.orderService.placeOrder(
      this.cartItems,
      deliveryAddress,
      'razorpay',
      isComboOrder,
      this.discount,
      paymentDetails,
      this.deliveryCharges
    ).subscribe({
      next: response => {
        // Reset loading state after order processing
        this.isLoading = false;
        
        if (response.success && response.order) {
          this.processSuccessfulOrder(response.order, 'Payment successful! Order placed successfully.');
        } else {
          // Payment succeeded but order creation failed - redirect to status page with error
          this.redirectToPaymentStatus('failed', null, 'Order could not be created. Please contact support.');
          this.isLoading = false;
        }
      },
      error:       error => {
        // Reset loading state on error
        this.isLoading = false;
        this.redirectToPaymentStatus('failed', null, 'Order creation failed. Your payment was successful. Please contact support.');
      }
    });
  }

  private logFailedPayment(
    paymentResponse: any, 
    deliveryAddress: string, 
    isComboOrder: boolean
  ): void {
    // You can implement additional logging here, such as:
    // - Send to analytics service
    // - Store in Firebase for analysis
    // - Send notification to admin
  }

  private processSuccessfulOrder(order: any, message: string): void {
    this.orderPlaced = true;
    this.paymentError = false; // Reset error state
    this.orderId = order.id;
    this.successMessage = message;
    
    // Clear cart after successful order
    this.cartService.clearCart();
    
    // Confirm order to start dose period
    this.confirmOrder(order.id);
    
    // Refresh dose eligibility to update cart screen immediately
    this.refreshDoseEligibility();
    
    // Redirect to payment status page with success info
    setTimeout(() => {
      this.redirectToPaymentStatus('success', {
        payment_id: order.razorpayPaymentId || '',
        order_id: order.id
      }, message);
    }, 500);
  }

  private refreshDoseEligibility(): void {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      this.authService.checkDosePurchaseEligibility(currentUser.id).subscribe({
        next: (eligibility) => {
          // The cart component will automatically pick up the updated eligibility
        },
        error: (error) => {
          // Silently fail
        }
      });
    }
  }

  confirmOrder(orderId: string): void {
    this.orderService.confirmOrder(orderId).subscribe(response => {
      if (response.success) {
        this.toastService.success('Order confirmed! Your dose period has started.');
      } else {
        this.toastService.warning('Order placed but confirmation failed. Please contact support.');
      }
    });
  }

  getFormattedAddress(): string {
    const form = this.checkoutForm.value;
    return `${form.fullName}, ${form.address}, ${form.city}, ${form.state} - ${form.pincode}. Mobile: ${form.mobile}`;
  }

  // Utility Methods
  getDoseColor(doseType: string): string {
    switch(doseType) {
      case 'green': return '#4CAF50';
      case 'yellow': return '#FFC107';
      case 'red': return '#F44336';
      default: return '#6b7280';
    }
  }

  getDoseDisplayName(doseType: string): Observable<string> {
    return this.doseColorService.getDoseDisplayName(doseType as DoseType);
  }

  getItemTotal(item: CartItem): number {
    return (item.price ?? 0) * item.cartQuantity;
  }

  getPaymentMethodName(): string {
    if (this.selectedPaymentMethod === 'cod') {
      return this.t('cashOnDelivery');
    } else if (this.selectedPaymentMethod === 'vyapargateway') {
      return 'UPI Payment (VyaparGateway)';
    } else if (this.selectedPaymentMethod === 'razorpay') {
      return 'Card/Net Banking/Wallet (Razorpay)';
    }
    return this.t('onlinePayment');
  }

  goToHome(): void {
    this.router.navigate(['/home']);
  }

  // Get dose type name
  getDoseTypeName(doseType: DoseType): string {
    const doseNames: { [key: string]: string } = {
      'morning': 'Morning',
      'afternoon': 'Afternoon',
      'evening': 'Evening',
      'night': 'Night'
    };
    return doseNames[doseType] || doseType;
  }

  // Payment utility methods
  isRazorpayReady(): boolean {
    return this.razorpayService.isRazorpayReady();
  }

  async waitForRazorpayReady(): Promise<void> {
    try {
      await this.razorpayService.waitForRazorpayReady();
    } catch (error) {
      throw error;
    }
  }

  private checkRazorpayStatus(): void {
    // Check Razorpay status and show appropriate message
      // The service will handle waiting and show toast messages
  }

  getTestPaymentInfo(): any {
    // Only show test info in development mode
    if (!environment.production && this.razorpayService.isTestMode()) {
      return {
        cards: this.razorpayService.getTestCardDetails(),
        note: 'Use these test credentials for payment testing'
      };
    }
    return null;
  }

  formatPaymentAmount(amount: number): string {
    return this.razorpayService.formatAmount(amount);
  }

  validatePaymentForm(): boolean {
    const form = this.checkoutForm.value;
    
    if (!this.razorpayService.validateAmount(this.total)) {
      this.toastService.error('Invalid payment amount. Please check your order total.');
      return false;
    }

    if (!this.razorpayService.validatePhoneNumber(form.mobile)) {
      this.toastService.error('Invalid phone number format. Please enter a valid 10-digit mobile number.');
      return false;
    }

    if (this.currentUser?.email && !this.razorpayService.validateEmail(this.currentUser.email)) {
      this.toastService.error('Invalid email format. Please check your email address.');
      return false;
    }

    return true;
  }
  
  // Navigation
  continueShopping(): void {
    this.router.navigate(['/products']);
  }

  viewProfile(): void {
    this.router.navigate(['/profile']);
  }

  trackOrder(): void {
    this.router.navigate(['/orders'], { queryParams: { order: this.orderId } });
  }

  // Form Control Getters
  get fullName() { return this.checkoutForm.get('fullName'); }
  get mobile() { return this.checkoutForm.get('mobile'); }
  get altMobile() { return this.checkoutForm.get('altMobile'); }
  get address() { return this.checkoutForm.get('address'); }
  get city() { return this.checkoutForm.get('city'); }
  get state() { return this.checkoutForm.get('state'); }
  get pincode() { return this.checkoutForm.get('pincode'); }
  get paymentMethod() { return this.checkoutForm.get('paymentMethod'); }

  private rebuildPaymentMethods(): void {
    this.paymentMethods = [
      {
        id: 'online',
        name: this.t('secureOnlinePayment') || 'Online Payment',
        description: this.t('paySecurelyWithCardsNetBankingOrWallets') || 'Pay securely with Cards, Net Banking, UPI or Wallets',
        icon: 'fas fa-shield-alt'
      },
      {
        id: 'cod',
        name: 'Cash on Delivery',
        description: 'Pay when you receive your order',
        icon: 'fas fa-money-bill-wave'
      }
    ];
  }

  // Localized product name
  getLocalizedName(item: CartItem): string {
    return this.productLocalizationService.getLocalizedName(item);
  }

  // Localized product description
  getLocalizedDescription(item: CartItem): string {
    return this.productLocalizationService.getLocalizedDescription(item);
  }

  // Get display name with category (Product Name + Category Name + Option X)
  getDisplayNameWithCategory(item: CartItem): string {
    const productName = this.getLocalizedName(item);
    const categoryName = item.selectedCategoryName;
    
    if (categoryName) {
      // Capitalize first letter of each word in category name
      const formattedCategoryName = categoryName
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      // Add option label if sub-product option is selected
      if (item.selectedSubProductIndex !== undefined && item.selectedSubProductIndex >= 0) {
        const optionLabel = this.getOptionLabel(item.selectedSubProductIndex);
        return `${productName} (${formattedCategoryName}, ${optionLabel})`;
      }
      
      return `${productName} (${formattedCategoryName})`;
    }
    return productName;
  }

  // Get option label (Option A, Option B, etc.)
  getOptionLabel(index: number): string {
    return `Option ${String.fromCharCode(65 + index)}`;
  }

  // Get sub-products for display (items in box) - show selected option text
  getSubProducts(item: CartItem): string {
    // If selected sub-product text exists, use it (this is the actual data for DB)
    if (item.selectedSubProductText) {
      return item.selectedSubProductText;
    }
    
    // Fallback to array of sub-products (backward compatibility)
    const subProducts: any = item.selectedCategorySubProducts;
    if (subProducts) {
      // Check if it's an array
      if (Array.isArray(subProducts) && subProducts.length > 0) {
        return subProducts.join(', ');
      }
      // If it's a string, return it directly
      if (typeof subProducts === 'string' && subProducts.length > 0) {
        return subProducts;
      }
    }
    return '';
  }

  // Get unit price for item (not total)
  getItemUnitPrice(item: CartItem): number {
    return item.price ?? 0;
  }

  // Get unit original price for item (not total)
  getItemUnitOriginalPrice(item: CartItem): number | undefined {
    return item.originalPrice;
  }

  // Check if item has offer price (original price > current price)
  hasOfferPrice(item: CartItem): boolean {
    const originalPrice = item.originalPrice;
    const currentPrice = item.price ?? 0;
    return originalPrice !== undefined && originalPrice !== null && originalPrice > 0 && originalPrice > currentPrice;
  }

  // Retry payment after failure
  retryPayment(): void {
    // Reset error state
    this.paymentError = false;
    this.errorMessage = '';
    this.isLoading = false;
    // Try to place the order again
    this.placeOrder();
  }

  // Redirect to payment status page
  private redirectToPaymentStatus(
    status: 'success' | 'failed' | 'cancelled',
    paymentResponse: any,
    message: string
  ): void {
    const params: any = {
      status: status,
      message: message
    };

    // Add payment details if available
    if (paymentResponse) {
      if (paymentResponse.payment_id) params.paymentId = paymentResponse.payment_id;
      if (paymentResponse.order_id) params.orderId = paymentResponse.order_id;
      if (paymentResponse.amount) params.amount = paymentResponse.amount;
    }

    // Add cart items count
    if (this.cartItems && this.cartItems.length > 0) {
      params.items = JSON.stringify(this.cartItems);
    }

    // Redirect to payment status page
    this.router.navigate(['/payment-status'], { queryParams: params });
  }
}
